/**
 * Marketing site layout — public, unauthenticated.
 *
 * VeraLok Public Presence Implementation (Creative Pack, 2026-08-30):
 * - Global navigation matches the WEBSITE_COPY_COMPLETE.md authority.
 * - Primary CTA: "Get VeraLok" → /citizen (app-store selector).
 * - Institutional CTA: "Engage VeraLok" → /engage.
 * - Footer carries the Delaware corporate address, social links, and
 *   the correct legal entity name (VeraLok Holdings Inc.).
 * - Website contact is form-first; no general public phone number.
 * - Do not surface a generic operator "Sign in" doorway. Operators
 *   authenticate via their portal subdomains.
 */
import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Linkedin, Facebook, Youtube } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { portalsForUser } from "@/portals/portalRouting";
import VeraLokLogo from "./VeraLokLogo";
import ChatWidget from "./ChatWidget";
import "./veralok-public-tokens.css";
import "./design-system.css";

const NAV = [
  { to: "/how-it-works",  label: "How It Works" },
  { to: "/institutions",  label: "Institutions" },
  { to: "/developers",    label: "Developers" },
  { to: "/trust-security", label: "Trust & Security" },
  { to: "/company",       label: "Company" },
  { to: "/resources",     label: "Resources" },
  { to: "/support",       label: "Support" },
];

const SOCIAL = [
  // Verified public destinations only. TikTok @veralok removed 2026-08-30
  // after confirmation it resolved to an unrelated private account. Do
  // not re-enable a TikTok link without first checking that the
  // logged-out destination matches the intended VeraLok company account.
  { href: "https://www.linkedin.com/company/veralok/",
    label: "VeraLok on LinkedIn",  Icon: Linkedin },
  { href: "https://www.facebook.com/profile.php?id=61593996629993",
    label: "VeraLok on Facebook",  Icon: Facebook },
  { href: "https://www.youtube.com/channel/UCT_kw29HU2AZQfRryHINMrA",
    label: "VeraLok on YouTube",   Icon: Youtube },
];

export default function MarketingLayout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const goPortal = () => {
    if (user) {
      const { primary } = portalsForUser(user);
      navigate(primary);
    } else {
      navigate("/login");
    }
  };
  return (
    <div className="vlk-marketing min-h-screen relative vlk-public" data-testid="marketing-layout">
      <div className="vlk-hero-canvas" aria-hidden="true" />
      <header className="border-b border-white/8 sticky top-0 z-30 backdrop-blur-md"
              style={{ background: "rgba(1,15,35,0.82)" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4 relative z-10">
          <Link to="/" data-testid="marketing-logo">
            <VeraLokLogo size={30} />
          </Link>
          <nav className="hidden lg:flex items-center gap-1 text-sm">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to}
                       className={({ isActive }) =>
                         `whitespace-nowrap px-3 py-1.5 rounded-full transition ${
                           isActive ? "text-white bg-white/10" : "text-white/60 hover:text-white"
                         }`}
                       data-testid={`marketing-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/engage" className="hidden md:inline-flex vlk-btn-ghost whitespace-nowrap"
                  data-testid="marketing-nav-engage">
              Engage VeraLok
            </Link>
            <Link to="/citizen" className="hidden md:inline-flex vlk-btn-primary whitespace-nowrap"
                  data-testid="marketing-nav-get-veralok">
              Get VeraLok
            </Link>
            {user && (
              <button onClick={goPortal} className="vlk-btn-primary whitespace-nowrap"
                      data-testid="marketing-nav-portal">
                Open portal
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden ml-1 p-2 rounded-full text-white/80 hover:bg-white/10"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              data-testid="marketing-mobile-menu-toggle"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <nav
            className="lg:hidden border-t border-white/8 backdrop-blur-md"
            style={{ background: "rgba(1,15,35,0.92)" }}
            data-testid="marketing-mobile-nav"
          >
            <div className="max-w-6xl mx-auto px-6 py-3 flex flex-col gap-1 text-sm">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `px-3 py-2 rounded-lg transition ${
                      isActive ? "text-white bg-white/10" : "text-white/70 hover:text-white hover:bg-white/5"
                    }`
                  }
                  data-testid={`marketing-mobile-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/engage"
                onClick={() => setMobileOpen(false)}
                className="mt-2 vlk-btn-ghost w-full justify-center"
                data-testid="marketing-mobile-nav-engage"
              >
                Engage VeraLok
              </Link>
              <Link
                to="/citizen"
                onClick={() => setMobileOpen(false)}
                className="mt-2 vlk-btn-primary w-full justify-center"
                data-testid="marketing-mobile-nav-get-veralok"
              >
                Get VeraLok
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-8 py-20 relative z-10">{children}</main>

      <footer className="border-t border-white/8 mt-16" data-testid="marketing-footer">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-semibold mb-3" style={{ fontFamily: "var(--vlk-font-display)" }}>Product</div>
            <ul className="space-y-1.5 text-white/60">
              <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
              <li><Link to="/citizen" className="hover:text-white">Citizen App</Link></li>
              <li><Link to="/institutions" className="hover:text-white">Institutions</Link></li>
              <li><Link to="/developers" className="hover:text-white">Developers</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3" style={{ fontFamily: "var(--vlk-font-display)" }}>Trust</div>
            <ul className="space-y-1.5 text-white/60">
              <li><Link to="/trust-security" className="hover:text-white">Trust &amp; Security</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/security-report" className="hover:text-white">Security Reporting</Link></li>
              <li><Link to="/status" className="hover:text-white">Status</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3" style={{ fontFamily: "var(--vlk-font-display)" }}>Company</div>
            <ul className="space-y-1.5 text-white/60">
              <li><Link to="/company" className="hover:text-white">Company</Link></li>
              <li><Link to="/leadership" className="hover:text-white">Leadership</Link></li>
              <li><Link to="/resources" className="hover:text-white">Resources / Media</Link></li>
              <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3" style={{ fontFamily: "var(--vlk-font-display)" }}>Legal</div>
            <ul className="space-y-1.5 text-white/60">
              <li><Link to="/legal/privacy" className="hover:text-white">Privacy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-white">Terms</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-white">Cookies</Link></li>
              <li><Link to="/legal/security" className="hover:text-white">Security &amp; Compliance</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <address className="not-italic text-xs text-white/50 leading-relaxed" data-testid="marketing-footer-address">
            VeraLok Holdings Inc.<br />
            251 Little Falls Drive<br />
            Wilmington, Delaware 19808<br />
            United States
          </address>
          <div className="flex items-center gap-3" data-testid="marketing-footer-social">
            {SOCIAL.map(({ href, label, Icon, text }) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                 aria-label={label}
                 className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
                 data-testid={`marketing-social-${label.split(" ").pop().toLowerCase()}`}>
                {Icon ? <Icon className="h-4 w-4" /> : <span className="text-xs font-semibold">{text}</span>}
              </a>
            ))}
          </div>
        </div>
        <div className="border-t border-white/5 text-center text-xs py-4 text-white/40">
          © 2026 VeraLok Holdings Inc. — All rights reserved.
        </div>
      </footer>
      <ChatWidget />
    </div>
  );
}
